import React, { useEffect, useState, useCallback } from "react";
import { X, Download, Share, PlusSquare } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const DISMISS_KEY = "fs_install_dismissed_at";
const DISMISS_DAYS = 7;

function isIos() {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS never fires beforeinstallprompt — show manual instructions banner
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 2500);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setShowIosHelp(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (err) {
      console.warn("localStorage unavailable", err);
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") setVisible(false);
      } catch (err) {
        console.warn("install prompt failed", err);
      }
      setDeferredPrompt(null);
    } else if (isIos()) {
      setShowIosHelp(true);
    }
  }, [deferredPrompt]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
          className="fixed left-4 right-4 z-50 mx-auto max-w-md"
          style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
          data-testid="install-prompt"
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
            {!showIosHelp ? (
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500">
                  <Download className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900">
                    Install FileShare
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    Get the app on your device — fast & fullscreen
                  </p>
                </div>
                <button
                  data-testid="install-app-btn"
                  onClick={handleInstall}
                  className="shrink-0 rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600"
                >
                  Install
                </button>
                <button
                  data-testid="install-dismiss-btn"
                  onClick={dismiss}
                  aria-label="Dismiss"
                  className="shrink-0 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div data-testid="ios-install-help">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">
                    Install on iPhone / iPad
                  </p>
                  <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <ol className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-600">1</span>
                    Tap the <Share className="inline h-4 w-4 text-blue-500" /> Share button in Safari
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-600">2</span>
                    Scroll and tap <PlusSquare className="inline h-4 w-4 text-blue-500" /> {'"Add to Home Screen"'}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-600">3</span>
                    Tap {'"Add"'} — FileShare appears on your home screen
                  </li>
                </ol>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
